import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Table,
  TableBody,
  TableRow,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useContext, useEffect, useState } from 'react';

import { Path, StatusLabel, ButtonLoading, SmartTableCell } from '@shared/components';

import { TItem } from '@modules/items';
import { AccessType } from '@modules/access';

import { useFolder } from '../../api/folders.api';
import {
  EFolderType,
  isTFolder,
  IFolder,
  TPath,
  TRootItems,
  TSelectFolderDialogState,
} from '../../types/folders.types';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { SignInContext } from '@modules/users';
import { TNavigatableFolderType } from '../../types/dialogs.types';
import { isCountryItem, isSnapshotItem, isNavigatableFolderType } from '../../helpers/items.helper';

type TProps = {
  title: string;
  buttonLabel: string;
  sourceId: string;
  navigatableFolderTypes: TNavigatableFolderType[];
  requireFs: boolean;
  disabledFolderTypes?: EFolderType[];
  state?: TSelectFolderDialogState;
  setState: (state: TSelectFolderDialogState) => void;
  rootFolderId?: string;
};

type TPropsSelectFolderContent = {
  navigatableFolderTypes: TNavigatableFolderType[];
  data: IFolder | TRootItems;
  affectedItems: TItem[];
  setTargetId: (targetId: string) => void;
  rootFolderId?: string;
};

const useStyles = makeStyles({
  tableBody: {
    '& > :last-child': {
      borderBottom: 'none',
    },
  },
  dialogContent: {
    height: '60vh',
    overflowY: 'auto',
  },
});

const SelectFolderContent = ({
  navigatableFolderTypes,
  data,
  affectedItems,
  setTargetId,
  rootFolderId,
}: TPropsSelectFolderContent) => {
  const { organization } = useContext(SignInContext);
  // const isOnlyBlockNavigable = navigatableFolderTypes.length === 1 && navigatableFolderTypes[0] === 'BLOCK';
  // const isOnlyCountryNavigable = navigatableFolderTypes.length === 1 && navigatableFolderTypes[0] === 'COUNTRY';

  const pathItems: TPath = [{ _id: '', name: organization.name, accessRight: AccessType.PARTIAL }];
  const isFolder = isTFolder(data);
  if (isFolder) {
    pathItems.push(...data.resolvedPath, { _id: data._id, name: data.name, accessRight: data.accessRight });

    // if (isOnlyBlockNavigable || isOnlyCountryNavigable)
    //   pathItems.splice(2);  // remove the organization root and the product folder
  }

  // limit path:
  if (rootFolderId) {
    const i = pathItems.findIndex((i) => i._id === rootFolderId);
    if (i !== -1) pathItems.splice(i);
    else console.warn(`Root folder with ID ${rootFolderId} not found in path items.`);
  }

  const items = data.children
    .filter((item) => isNavigatableFolderType(data, item, navigatableFolderTypes))
    .sort(
      (itemA, itemB) =>
        (itemB._id === organization.folders.qm ? 1 : 0) - (itemA._id === organization.folders.qm ? 1 : 0)
    );

  const classes = useStyles();
  return (
    <div>
      <Path
        items={useFolderPathAsBreadcrumbs(pathItems)}
        hasCurrent
        onClick={(idx) => setTargetId(pathItems[idx]._id)}
      />
      <Table>
        <TableBody className={classes.tableBody}>
          {items.map((item) => (
            <TableRow key={item._id}>
              <SmartTableCell
                variant={{ kind: 'item', item }}
                // selecting items or the current folder is not allowed
                linkDisabled={item.type !== 'FOLDER' || Boolean(affectedItems.find((a) => a._id === item._id))}
                onLinkClick={() => {
                  setTargetId(item._id);
                }}
                tooltip={{ positioning: 'after', threshold: 60 }}
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const SelectFolderDialog = ({
  title,
  buttonLabel,
  sourceId,
  navigatableFolderTypes,
  requireFs, // disable folders that cannot contain non-folder items
  disabledFolderTypes = [],
  state,
  setState,
  rootFolderId,
}: TProps) => {
  const { organization } = useContext(SignInContext);
  const [targetId, setTargetId] = useState<string>(sourceId);

  useEffect(() => {
    if (state?.isOpen) setTargetId(sourceId);
  }, [state?.isOpen]);

  const { error, data, isLoading: isLoadingFolder } = useFolder(targetId);

  const disabled =
    !data ||
    !isTFolder(data) ||
    !(data.accessRight >= AccessType.EDIT) ||
    (requireFs &&
      ((data.resolvedPath.length < 1 && data._id !== organization.folders.qm) || // disable product folder
        (data.resolvedPath.length < 3 && isSnapshotItem(data)) || // disable snapshot root and snapshot collection
        (data.resolvedPath.length < 2 && isCountryItem(data)))) || // disable countries root folder
    disabledFolderTypes.includes(data.folderType);

  const handleSelect = async () => {
    // async for button loading state
    if (!targetId) return;
    await state?.select(targetId);
  };

  const classes = useStyles();
  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_, data) => {
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <StatusLabel
              data={data}
              loadingMessage="Loading folder..."
              error={error}
              errorMessage="Loading folder failed!"
              render={(data) =>
                state && (
                  <SelectFolderContent
                    navigatableFolderTypes={navigatableFolderTypes}
                    data={data}
                    setTargetId={setTargetId}
                    affectedItems={state.affectedItems}
                    rootFolderId={rootFolderId}
                  />
                )
              }
            />
          </DialogContent>
          <DialogActions>
            <ButtonLoading appearance="primary" disabled={disabled} loading={isLoadingFolder} onClick={handleSelect}>
              {buttonLabel}
            </ButtonLoading>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SelectFolderDialog;
